import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    User,
    Baby,
    Package,
    // Calendar,
    // MapPin, // T205: used by commented Saved Addresses item
    // Settings, // #246: settings only via My account hub gear
    LogOut,
    Plus,
    Star,
    ChevronDown,
    // Truck,
} from 'lucide-react';
import { useFrontendAuth } from '../hooks/useAuth';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { getSellerSummary } from '../services/api.shop';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { publicProfileHeading } from '../utils/usernameRules';

interface MenuItemProps {
    to: string;
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    onClick?: () => void;
}

const MenuItem = ({ to, icon, title, subtitle, onClick }: MenuItemProps) => (
    <Link
        to={to}
        onClick={onClick}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_70%)]"
    >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-color)]/20 text-[var(--text-color)]">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--text-color)]">{title}</p>
            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">{subtitle}</p>
        </div>
    </Link>
);

const UserProfileDropdown = () => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const { user, logout } = useFrontendAuth();
    const [open, setOpen] = useState(false);

    const { data: sellerSummary } = useProtectedQuery({
        queryKey: ['sellerSummary', user?.id],
        queryFn: () => getSellerSummary(user!.id),
        enabled: !!user?.id,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!e.target || !(e.target as HTMLElement).closest('.profile-dropdown-root')) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [open]);

    if (!user) return null;

    const heading = publicProfileHeading({
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
    });
    const initials = (heading.title || user.email || '?')
        .replace(/^@/, '')
        .charAt(0)
        .toUpperCase();

    const handleLogout = async () => {
        setOpen(false);
        // logout() already navigates to /login with replace
        await logout();
    };

    const close = () => setOpen(false);

    return (
        <div className="profile-dropdown-root relative">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 rounded-full transition hover:bg-[var(--accent-color)]/20 px-1 py-0.5"
                aria-expanded={open}
                aria-haspopup="true"
            >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--accent-color)] text-xs font-bold text-white shadow-sm">
                    {initials}
                </div>
                {heading.title && (
                    <span className="hidden sm:inline text-sm font-medium text-[var(--text-color)]">
                        {heading.title.replace(/^@/, '').split(' ')[0]}
                    </span>
                )}
                <ChevronDown className={`h-3.5 w-3.5 text-[var(--text-color)]/50 transition ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute right-0 top-full z-50 mt-2 panel-edge-safe md:w-72 max-h-[min(80dvh,32rem)] overflow-y-auto rounded-2xl border border-[var(--accent-color)]/40 bg-[var(--bg-color)] p-4 shadow-xl">
                    <div className="mb-4 flex items-center gap-3 border-b border-[var(--accent-color)]/25 pb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-color)] text-lg font-bold text-white">
                            {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold text-[var(--text-color)]">{heading.title}</p>
                            {heading.subtitle && (
                                <p className="truncate text-xs text-[var(--text-color)]">{heading.subtitle}</p>
                            )}
                            <p className="truncate text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">{user.email}</p>
                            {sellerSummary && sellerSummary.ratingCount > 0 && (
                                <p className="mt-0.5 flex items-center gap-1 text-xs text-[var(--text-color)]">
                                    <Star className="h-3 w-3 fill-[var(--accent-color)] text-[var(--accent-color)]" />
                                    <span className="font-medium">{sellerSummary.averageRating.toFixed(1)}</span>
                                    <span className="opacity-60">
                                        {tr('header.dropdown.reviews', '{count} reviews').replace('{count}', String(sellerSummary.ratingCount))}
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>

                    <nav className="flex flex-col gap-0.5">
                        {/* #246: Journey → Listings → Account hub (purchases/sales/settings live on hub) */}
                        <MenuItem
                            to="/my-petito"
                            icon={<Baby className="h-4 w-4" />}
                            title={tr('header.dropdown.journey', 'My Journey')}
                            subtitle={tr('header.dropdown.journey.sub', 'Personalized for your baby')}
                            onClick={close}
                        />
                        <MenuItem
                            to="/my-account/listings"
                            icon={<Package className="h-4 w-4" />}
                            title={tr('header.dropdown.listings', 'My listings')}
                            subtitle={tr(
                                'header.dropdown.listings.sub',
                                'Items you sell'
                            )}
                            onClick={close}
                        />
                        <MenuItem
                            to="/my-account"
                            icon={<User className="h-4 w-4" />}
                            title={tr('header.dropdown.profile', 'My account')}
                            subtitle={tr(
                                'header.dropdown.profile.sub',
                                'Purchases, sales, settings & more'
                            )}
                            onClick={close}
                        />
                        {/* #246 commented: Settings — open from My account profile gear
                        <MenuItem
                            to="/my-account?tab=settings"
                            icon={<Settings className="h-4 w-4" />}
                            title={tr('header.dropdown.settings', 'Settings')}
                            subtitle={tr('header.dropdown.settings.sub', 'Preferences & privacy')}
                            onClick={close}
                        />
                        */}
                        {/* #246 commented: Following — open from My account hub
                        <MenuItem
                            to="/my-account?tab=following"
                            icon={<User className="h-4 w-4" />}
                            title={tr('profile.tab.following', 'Following')}
                            subtitle={tr(
                                'header.dropdown.following.sub',
                                'People you follow'
                            )}
                            onClick={close}
                        />
                        */}
                        {/* #246 commented: Purchases — hub activity card
                        <MenuItem
                            to="/my-account/orders"
                            icon={<Calendar className="h-4 w-4" />}
                            title={tr('header.dropdown.purchases', 'Purchases')}
                            subtitle={tr('header.dropdown.purchases.sub', 'Order history')}
                            onClick={close}
                        />
                        */}
                        {/* #246 commented: Sales — hub activity card
                        <MenuItem
                            to="/my-account/sales"
                            icon={<Truck className="h-4 w-4" />}
                            title={tr('header.dropdown.sales', 'Sales')}
                            subtitle={tr('header.dropdown.sales.sub', 'Ship sold items')}
                            onClick={close}
                        />
                        */}
                        {/* T205: Saved Addresses — edit via profile details for now; keep for later dedicated page
                        <MenuItem
                            to="/my-account?tab=settings"
                            icon={<MapPin className="h-4 w-4" />}
                            title={tr('header.dropdown.addresses', 'Saved Addresses')}
                            subtitle={tr('header.dropdown.addresses.sub', 'Delivery locations')}
                            onClick={close}
                        />
                        */}
                    </nav>

                    <div className="mt-4 space-y-2 border-t border-[var(--accent-color)]/25 pt-4">
                        <Link
                            to="/sell"
                            onClick={close}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-color)] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                        >
                            <Plus className="h-4 w-4" />
                            {tr('header.dropdown.sell', 'Sell Item')}
                        </Link>
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="flex w-full items-center justify-center gap-2 py-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)] transition hover:text-[var(--text-color)]"
                        >
                            <LogOut className="h-4 w-4" />
                            {tr('header.dropdown.logout', 'Log out')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfileDropdown;